import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSurveyConfig, GRADE_LEVEL_TO_SURVEY } from '../config/surveys';
import './SurveyDashboard.css';

export default function SurveyDashboard() {
  const { user, availableSurvey, completedSurveys, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return null;
  }

  // Determine survey ID from availableSurvey or user's surveyType
  const surveyId = availableSurvey ||
    GRADE_LEVEL_TO_SURVEY[user.surveyType as keyof typeof GRADE_LEVEL_TO_SURVEY] ||
    null;

  // Get survey config from registry
  const surveyConfig = surveyId ? getSurveyConfig(surveyId) : null;

  // Debug logging - remove after fixing
  console.log('[Dashboard] Debug:', {
    availableSurvey,
    userSurveyType: user.surveyType,
    surveyId,
    surveyConfig: surveyConfig ? 'found' : 'null',
    GRADE_LEVEL_TO_SURVEY,
  });

  const hasCompleted = surveyId ? completedSurveys.includes(surveyId) : false;

  const handleStartSurvey = () => {
    if (surveyId) {
      navigate(`/survey/${surveyId}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getGradeLabel = () => {
    if (user.gradeLevel === null) return 'Unknown';
    if (user.gradeLevel === 0) return 'Kindergarten';
    return `Grade ${user.gradeLevel}`;
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        {/* Header with user info */}
        <header className="dashboard-header">
          <div className="user-info">
            <div className="user-details">
              <h2>Welcome, {user.name.split(' ')[0]}!</h2>
              <p>{user.email}</p>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </header>

        {/* Grade info */}
        <div className="grade-badge">
          <span className="grade-label">{getGradeLabel()}</span>
          {user.schoolName && <span className="school-type">{user.schoolName}</span>}
        </div>

        {/* Survey section */}
        <div className="survey-section">
          {hasCompleted ? (
            <div className="all-caught-up">
              <div className="caught-up-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>All Caught Up!</h3>
              <p>You have no surveys left to complete.</p>
            </div>
          ) : surveyConfig ? (
            <>
              <h3>Your Survey</h3>
              <div className="survey-card available">
                <div className="survey-info">
                  <h4>{surveyConfig.name}</h4>
                  <p>{surveyConfig.metadata?.description || 'Share your feedback about school'}</p>
                </div>
                <button className="start-survey-btn" onClick={handleStartSurvey}>
                  Start Survey
                </button>
              </div>
            </>
          ) : (
            <div className="all-caught-up">
              <div className="caught-up-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>No Surveys Available</h3>
              <p>Check back later or contact your school administrator if you believe this is an error.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="dashboard-footer">
          <p>
            Questions about the survey? Contact your school administrator.
          </p>
        </footer>
      </div>
    </div>
  );
}
